import React, { Component } from 'react';

import Downshift from 'downshift';
import differenceWith from 'lodash.differencewith';
import isEqual from 'lodash.isequal';
import PropTypes from 'prop-types';

import { useLocale, pickKeys } from '@adsk/alloy-react-helpers';
import Overlay from '@adsk/alloy-react-overlay';

import {
  CREATE_TYPE,
  defaultMenuRenderer,
  defaultValueRenderer,
  defaultValueTagRenderer,
  defaultLabelRenderer,
  defaultOptionRenderer,
  defaultCreateOptionRenderer,
  defaultItemRenderer,
  defaultFilterOptions,
  defaultSelectBoxRenderer,
  defaultGroupHeaderRenderer,
} from './layout/defaultRenderers';
import SelectBoxControl from './layout/SelectBoxControl';
import SelectBoxMenu from './layout/SelectBoxMenu';

/**
 * SelectBox
 */
class SelectBox extends Component {
  constructor(props) {
    super(props);

    this.stateReducer = this.stateReducer.bind(this);
    this.getOptionLabel = this.getOptionLabel.bind(this);
    this.isSameItem = this.isSameItem.bind(this);
    this.itemToString = this.itemToString.bind(this);
    this.handleUserAction = this.handleUserAction.bind(this);
    this.handleInputValueChange = this.handleInputValueChange.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.renderSelectBox = this.renderSelectBox.bind(this);
    this.ref = React.createRef();
  }

  componentDidMount() {
    const { container, autoFocus, isOpen } = this.props;
    // for initial rendering the `target` for overlay is undefined
    if (container && (autoFocus || isOpen)) {
      this.forceUpdate();
    }
  }

  render() {
    const {
      defaultValue,
      defaultInputValue,
      defaultHighlightedIndex,
      defaultIsOpen,
      value,
      isOpen,
      options,
      searchable,
      autoFocus,
      inputValue,
      onStateChange,
    } = this.props;

    const initialIsOpen =
      searchable && autoFocus && isOpen === undefined ? true : defaultIsOpen;

    return (
      <Downshift
        itemCount={options.length}
        itemToString={this.itemToString}
        stateReducer={this.stateReducer}
        onUserAction={this.handleUserAction}
        onInputValueChange={this.handleInputValueChange}
        onChange={this.handleChange}
        onStateChange={onStateChange}
        initialSelectedItem={defaultValue}
        initialInputValue={
          searchable && !initialIsOpen ? defaultInputValue : ''
        }
        initialHighlightedIndex={defaultHighlightedIndex}
        initialIsOpen={initialIsOpen}
        selectedItem={value}
        inputValue={searchable && !initialIsOpen ? inputValue : undefined}
        isOpen={isOpen}
      >
        {this.renderSelectBox}
      </Downshift>
    );
  }

  stateReducer(state, _changes) {
    const {
      multiple,
      searchable,
      creatable,
      clearInputOnSelect,
      stateReducer,
    } = this.props;
    const changes = { ..._changes };

    // stay open on clear
    if (state.isOpen && changes.selectedItem === null) {
      changes.isOpen = true;
    }
    // don't reset input if value is controlled
    if (
      multiple &&
      searchable &&
      state.isOpen &&
      changes.type ===
        Downshift.stateChangeTypes.controlledPropUpdatedSelectedItem
    ) {
      changes.inputValue = state.inputValue;
    } else if (
      multiple &&
      (changes.type === Downshift.stateChangeTypes.clickItem ||
        changes.type === Downshift.stateChangeTypes.keyDownEnter)
    ) {
      changes.isOpen = true;

      const selectedItems = Array.isArray(state.selectedItem)
        ? state.selectedItem
        : [];
      const { selectedItem } = changes;
      const selected = selectedItems.find((x) =>
        this.isSameItem(x, selectedItem)
      );
      const items = selected
        ? selectedItems.filter((x) => !this.isSameItem(x, selectedItem))
        : [...selectedItems, selectedItem];

      changes.selectedItem = items;

      if (searchable) {
        changes.inputValue = !clearInputOnSelect ? state.inputValue : '';

        // if creating, clear the input value
        if (
          changes.inputValue &&
          creatable &&
          !selected &&
          _changes.selectedItem &&
          _changes.selectedItem[CREATE_TYPE]
        ) {
          changes.inputValue = '';
        }
      }
    }
    if (!searchable) {
      delete changes.inputValue;
    } else if (!state.isOpen && changes.isOpen) {
      changes.inputValue = '';
      changes.highlightedIndex = 0;
    }

    // auto select the first item when searching
    if (changes.inputValue && !changes.highlightedIndex) {
      changes.highlightedIndex = 0;
    }

    // do not deselect item when searching
    if (
      multiple &&
      searchable &&
      state.isOpen &&
      changes.type === Downshift.stateChangeTypes.keyDownEnter &&
      changes.selectedItem?.length < state.selectedItem?.length
    ) {
      changes.selectedItem = state.selectedItem;
    }

    if (stateReducer) {
      return stateReducer(state, changes);
    }
    return changes;
  }

  getOptionLabel(item) {
    const { labelGetter, labelKey } = this.props;
    return labelGetter ? labelGetter(item) : item[labelKey] || '';
  }

  isSameItem(a, b) {
    const { isSameOption, valueKey } = this.props;
    if (isSameOption) return isSameOption(a, b);
    return a[valueKey] === b[valueKey];
  }

  itemToString(item) {
    const { multiple, showValueAsTags } = this.props;

    if (!item || showValueAsTags) return '';
    if (!multiple) return this.getOptionLabel(item);

    if (item.length === 0) return '';
    if (item.length === 1) return this.getOptionLabel(item[0]);

    return this.props['T'].translate('SELECTED', {
      count: item.length,
    });
  }

  isSelected(selectedItem) {
    const { multiple } = this.props;
    return multiple
      ? Array.isArray(selectedItem) && selectedItem.length > 0
      : !!selectedItem;
  }

  // if the option has only key but no label, find the option from options
  normalizeValue(selectedItem) {
    const { multiple, options, valueKey } = this.props;

    if (multiple) {
      if (!Array.isArray(selectedItem)) return [];

      return selectedItem.map((item) => {
        if (!this.getOptionLabel(item)) {
          return options.find((x) => x[valueKey] === item[valueKey]) || item;
        }
        return item;
      });
    } else {
      if (!selectedItem || selectedItem[valueKey] === undefined) return null;

      if (!this.getOptionLabel(selectedItem)) {
        return (
          options.find((x) => x[valueKey] === selectedItem[valueKey]) ||
          selectedItem
        );
      }
      return selectedItem;
    }
  }

  handleUserAction(changes) {
    if (changes.isOpen === true) {
      this.props.onOpen();
    }
    if (changes.isOpen === false) {
      this.props.onClose();
    }
  }

  handleInputValueChange(inputValue, stateAndHelpers) {
    if (stateAndHelpers.type === Downshift.stateChangeTypes.changeInput) {
      this.props.onInputChange(inputValue, stateAndHelpers);
    }
  }

  handleChange(value, stateAndHelpers) {
    const { labelKey, creatable, onChange, onCreate } = this.props;
    if (creatable) {
      if (
        Array.isArray(value) &&
        value.length > 0 &&
        value[value.length - 1][CREATE_TYPE]
      ) {
        onCreate(value[value.length - 1][labelKey], stateAndHelpers);
        return;
      }
      if (value && value[CREATE_TYPE]) {
        onCreate(value[labelKey], stateAndHelpers);
        return;
      }
    }
    onChange(value, stateAndHelpers);
  }

  renderSelectBox(stateAndHelpers) {
    const { isOpen, selectedItem } = stateAndHelpers;
    const { multiple, options, keepSelectedOnOptionsChange } = this.props;
    const isSelected = this.isSelected(selectedItem);
    let selectedValue = this.normalizeValue(selectedItem);

    const resetStaleSelectedValue =
      !keepSelectedOnOptionsChange && multiple && selectedValue.length > 0;
    if (resetStaleSelectedValue) {
      const difference = differenceWith(selectedValue, options, isEqual);
      if (difference.length > 0) {
        selectedValue = [];
      }
    }

    const { ['data-testid']: dataTestId, invalid } = this.props;

    return (
      <div style={{ width: '100%' }}>
        <div
          css={{
            position: 'relative',
          }}
          data-testid={dataTestId}
        >
          <SelectBoxControl
            {...this.props}
            invalid={invalid}
            isSelected={isSelected}
            selectedValue={selectedValue}
            stateAndHelpers={stateAndHelpers}
            innerRef={this.ref}
          />
          {isOpen && (
            <SelectBoxMenu
              isSameItem={this.isSameItem}
              {...this.props}
              className={this.props.menuClassName}
              style={this.props.menuStyle}
              isSelected={isSelected}
              selectedValue={selectedValue}
              stateAndHelpers={stateAndHelpers}
              target={this.ref}
              noResultsPlaceholder={this.props.noResultsPlaceholder}
            />
          )}
        </div>
      </div>
    );
  }
}

SelectBox.stateChangeTypes = Downshift.stateChangeTypes;
SelectBox.CREATE_TYPE = CREATE_TYPE;
SelectBox.PLACEMENTS = Overlay.PLACEMENTS;

SelectBox.displayName = 'SelectBox';

SelectBox.defaultProps = {
  options: [],
  valueKey: 'value',
  labelKey: 'label',
  subtextKey: 'subtext',
  defaultInputValue: '',
  placement: SelectBox.PLACEMENTS.BOTTOM_START,
  maxMenuHeight: 300,
  keepSelectedOnOptionsChange: true,
  noResultsPlaceholder: true,
  onChange: () => {},
  onCreate: () => {},
  onInputChange: () => {},
  onOpen: () => {},
  onClose: () => {},
};

SelectBox.propTypes = {
  ...pickKeys(Overlay.propTypes, ['container', 'placement']),
  /**
   * i18n helper for T.translate('KEY')
   */
  T: PropTypes.object,
  /**
   * The locale to use for translated strings
   */
  locale: PropTypes.string,
  /**
   * Class name for the component
   */
  className: PropTypes.string,
  /**
   * style for the component
   */
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  /**
   * Controlled selected value for the component
   */
  options: PropTypes.arrayOf(PropTypes.object),
  /**
   * Default selected value for the component
   */
  defaultValue: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.arrayOf(PropTypes.object),
  ]),
  /**
   * Controlled selected value for the component
   */
  value: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.arrayOf(PropTypes.object),
  ]),
  /**
   * Default input value for the component
   */
  defaultInputValue: PropTypes.string,
  /**
   * Controlled input value for the component
   */
  inputValue: PropTypes.string,
  /**
   * Default highlighted index for the component
   */
  defaultHighlightedIndex: PropTypes.number,
  /**
   * Controlled highlighted index for the component
   */
  highlightedIndex: PropTypes.number,
  /**
   * Whether the menu is open by default
   */
  defaultIsOpen: PropTypes.bool,
  /**
   * Whether the menu is open by controlled
   */
  isOpen: PropTypes.bool,
  /**
   * Whether the component is focused on mount
   */
  autoFocus: PropTypes.bool,
  /**
   * Whether the component is multi selectable
   */
  multiple: PropTypes.bool,
  /**
   * When the component is multi selectable whether
   * the input should keep the selected values
   * when the options change
   */
  keepSelectedOnOptionsChange: PropTypes.bool,
  /**
   * Whether the component is disabled
   */
  disabled: PropTypes.bool,
  /**
   * Whether the component is invalid
   */
  invalid: PropTypes.bool,
  /**
   * Whether the component is clearable
   */
  clearable: PropTypes.bool,
  /**
   * Whether the component is searchable
   */
  searchable: PropTypes.bool,
  /**
   * Whether to show search icon
   */
  showSearchIcon: PropTypes.bool,
  /**
   * Whether the component is creatable
   */
  creatable: PropTypes.bool,
  /**
   * Whether the component is loading options
   */
  isLoading: PropTypes.bool,
  /**
   * Whether to show value as tags
   */
  showValueAsTags: PropTypes.bool,
  /**
   * Whether to clear input on selected when searching
   */
  clearInputOnSelect: PropTypes.bool,
  /**
   * The key of the option value
   */
  valueKey: PropTypes.string,
  /**
   * The key of the option label
   */
  labelKey: PropTypes.string,
  /**
   * `(option) => string`, option label formatter
   */
  labelGetter: PropTypes.func,
  /**
   * The key of the option subtext
   */
  subtextKey: PropTypes.string,
  /**
   * `(option) => string`, option subtext formatter
   */
  subtextGetter: PropTypes.func,
  /**
   * The placeholder for the component
   */
  placeholder: PropTypes.node,
  /**
   * The placeholder for the component when searching
   */
  searchPlaceholder: PropTypes.node,
  /**
   * The content to be shown if the filtered options is empty
   */
  noResultsValue: PropTypes.node,
  /**
   * Class name for the overlay
   */
  overlayClassName: PropTypes.string,
  /**
   * style for the overlay
   */
  overlayStyle: PropTypes.object,
  /**
   * The max height of the menu, the footer is not included
   */
  maxMenuHeight: PropTypes.number,
  /**
   * `(state, changes) => object`, the most powerful function for the component, use it carefully
   */
  stateReducer: PropTypes.func,
  /**
   * `(optionA, optionsB) => boolean`, whether two options are equal
   */
  isSameOption: PropTypes.func,
  /**
   * `(options, inputValue, selectProps) => array`, filter options based on input value
   */
  filterOptions: PropTypes.func,
  /**
   * `(option, index, selectProps) => boolean`, whether the option is disabled
   */
  isOptionDisabled: PropTypes.func,
  /**
   * `(selectProps) => node`, custom footer renderer
   */
  footerRenderer: PropTypes.func,
  /**
   * `({ items, renderItem, selectedValue, highlightedIndex, selectProps }) => node`, custom menu renderer
   */
  menuRenderer: PropTypes.func,
  /**
   * `({ item, index, disabled, selected, itemProps, selectProps }) => node`, custom item renderer
   */
  itemRenderer: PropTypes.func,
  /**
   * `(value, selectProps) => node`, custom value renderer
   */
  valueRenderer: PropTypes.func,
  /**
   * `(item, selectProps) => node`, custom value tag renderer
   */
  valueTagRenderer: PropTypes.func,
  /**
   * `(option, selected, selectProps) => node`, custom option renderer
   */
  optionRenderer: PropTypes.func,
  /**
   * `(option, selected, selectProps) => node`, custom create option renderer
   */
  createOptionRenderer: PropTypes.func,
  /**
   * `(value, stateAndHelpers) => *`, callback when selected value changed
   */
  onChange: PropTypes.func,
  /**
   * `(inputValue, stateAndHelpers) => *`, callback when creating option
   */
  onCreate: PropTypes.func,
  /**
   * `(changes, stateAndHelpers) => *`, callback when internal state changed
   */
  onStateChange: PropTypes.func,
  /**
   * `(inputValue, stateAndHelpers) => *`, callback when searching
   */
  onInputChange: PropTypes.func,
  /**
   * `() => *`, callback when menu opened
   */
  onOpen: PropTypes.func,
  /**
   * `() => *`, callback when menu closed
   */
  onClose: PropTypes.func,
  /**
   * Class name for the menu
   */
  menuClassName: PropTypes.string,
  /**
   * Class name for the menu
   */
  menuStyle: PropTypes.object,
  /**
   * data-testid passed to the container element
   */
  'data-testid': PropTypes.string,
  /**
   * Flag passed to render the SelectboxMenu when no results are returned
   */
  noResultsPlaceholder: PropTypes.bool,
};

const WrapperSelectBox = (props) => {
  const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));

  return <SelectBox {...props} T={T} />;
};

WrapperSelectBox.stateChangeTypes = SelectBox.stateChangeTypes;
WrapperSelectBox.CREATE_TYPE = SelectBox.CREATE_TYPE;
WrapperSelectBox.PLACEMENTS = SelectBox.PLACEMENTS;

WrapperSelectBox.defaultMenuRenderer = defaultMenuRenderer;
WrapperSelectBox.defaultValueRenderer = defaultValueRenderer;
WrapperSelectBox.defaultValueTagRenderer = defaultValueTagRenderer;
WrapperSelectBox.defaultLabelRenderer = defaultLabelRenderer;
WrapperSelectBox.defaultOptionRenderer = defaultOptionRenderer;
WrapperSelectBox.defaultCreateOptionRenderer = defaultCreateOptionRenderer;
WrapperSelectBox.defaultItemRenderer = defaultItemRenderer;
WrapperSelectBox.defaultFilterOptions = defaultFilterOptions;
WrapperSelectBox.defaultSelectBoxRenderer = defaultSelectBoxRenderer;
WrapperSelectBox.defaultGroupHeaderRenderer = defaultGroupHeaderRenderer;

export default WrapperSelectBox;
